import { DOMEvents, DOMUtils } from '../../../../utils/dom'

import { IAdsConfiguration, ICommentAdConfiguration } from '../../../../domain/conf/ads'
import { Utils } from '../../../../utils/common'
import { logger } from '../../../../utils/common/logger'
import { Store } from '../../../../utils/storage/keys'
import { LocalStore } from '../../../../utils/storage/local'
import { AnalyticsService } from '../../../analytics'
import { IdentityEvent } from '../../../common/events/identity'
import { IProfile } from '../../../common/profile'
import { IViafouraWidget, ViafouraWidget } from './widget'

interface IWindow extends Window {
    vf: any
    vfQ: any
}

type Widget = 'commenting' | 'carousel' | 'conversationStarter' | 'reviews' | 'topicFollow'

declare let window: IWindow

export class Viafoura {
    public adLight: boolean
    public commentWidget: IViafouraWidget
    public carouselWidget: IViafouraWidget
    public conversationStarterWidget: IViafouraWidget
    public topicFollow: IViafouraWidget

    public displayAds = true
    public loadViafouraWidgets = false

    private DOM = {
        attrs: {
            jsUrl: '//cdn.viafoura.net/vf-v2.js',
            myTopics: 'data-my-topics',
            signOut: 'data-id-logout'
        },
        classes: {
            adLight: 'ad__adlight'
        },
        loginRadiusClasses: {
            emailValidation: 'email-validation-container'
        },
        loginRadiusIds: {
            forgotPassword: 'forgotpassword-container',
            login: 'login-container',
            registration: 'registration-container',
            resetPassword: 'resetpassword-container'
        },
        widgets: {
            contentRecirculation: 'vf-content-recirculation',
            conversationStarter: 'vf-conversation-starter',
            conversations: 'vf-conversations',
            dropdownItem: 'vf-dropdown__list-item',
            reviews: 'vf-livereviews',
            topicFollow: 'vf-topic-follow',
            trayTrigger: 'vf-tray-trigger',
        },
        wrappers: {
            carousel: {
                customEventHandlers: {
                    click: (_widget: ViafouraWidget, event: Event) => {
                        const target = event.target as HTMLElement
                        const articleAttributes = this.scrapeTargetUrlAndAnchorText(target)
                        // if targetUrl is empty, the click was not on any article in the widget, ex: the widget title
                        if (articleAttributes.targetUrl) {
                            document.dispatchEvent(
                                new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                                    detail: {
                                        anchor_text: articleAttributes.anchorText,
                                        click_source_type: 'widget-list',
                                        control_fields: {
                                            mparticle: {
                                                keys: {
                                                    anchor_text: 'anchor_text',
                                                    click_source_type: 'click_source_type',
                                                    layout_section: 'layout_section',
                                                    target_url: 'target_url',
                                                    widget_title: 'widget_title'
                                                },
                                                mp_event_type: 'Navigation'
                                            },
                                        },
                                        event_name: AnalyticsService.customEventNames.click,
                                        layout_section: 'most-commented',
                                        target_url: articleAttributes.targetUrl,
                                        widget_title: 'most-commented'
                                    },
                                })
                            )
                        }
                    }
                },
                selector: 'data-sso-carousel',
                title: 'Most Commented'
            },
            commenting: {
                attrs: {
                    commentsLimit: '',
                    commentsLoadMoreLimit: '',
                    firstPromoPosition: '1',
                    promoInterval: '10',
                    replyLimit: '',
                    signature: ''
                },
                customEventHandlers: {
                    click: (_widget: ViafouraWidget, event: Event) => {
                        const target = event.target as HTMLAnchorElement
                        /* istanbul ignore next */
                        if (
                            !target.innerHTML.includes('Load more comments') ||
                            target.getAttribute('data-evt-skip-click') === 'true'
                        ) {
                            return
                        }

                        const occurrence =
                            target.parentElement.classList.contains('vf-load-more') ||
                            target.parentElement.classList.contains('vf-loader-button__content')
                                ? 'other'
                                : 'first'
                        /* istanbul ignore next */
                        const customEvent = new CustomEvent('custom-event', {
                            detail: {
                                action_type: 'load_more',
                                context: 'comments',
                                control_fields: {
                                    mparticle: {
                                        keys: {
                                            action_type: 'action_type',
                                            context: 'context',
                                            occurrence: 'occurrence'
                                        },
                                        mp_event_type: 'Other',
                                    }
                                },
                                event_name: 'on_page_action_click',
                                occurrence
                            },
                        })
                        document.dispatchEvent(customEvent)
                    }
                },
                selector: 'data-sso-commenting',
                title: ''
            },
            conversationStarter: {
                customEventHandlers: {
                    click: (widget: ViafouraWidget, event: Event) => {
                        const target = event.target as HTMLAnchorElement
                        /* istanbul ignore next */
                        if (!target.classList.contains('vf-conversation-starter_link')) {
                            return
                        }
                        /* istanbul ignore next */
                        const customEvent = new CustomEvent('custom-event', {
                            detail: {
                                control_fields: {
                                    mparticle: {
                                        keys: {
                                            source_section: 'source_section',
                                            source_state: 'source_state',
                                            target_name: 'target_name'
                                        },
                                        mp_event_type: 'Other',
                                    }
                                },
                                event_name: 'click_to_section',
                                source_section: 'conversation_starter',
                                source_state: widget.getConversationStarterState(),
                                target_name: new URL(target.href).hash,
                            },
                        })
                        document.dispatchEvent(customEvent)
                    }
                },
                description: 'Leave a comment and share your thoughts with our community.',
                selector: 'data-sso-conversation-starter',
                target: 'comments',
                title: 'Have your say',
            },
            reviews: {
                customEventHandlers: {
                    click: (_widget: ViafouraWidget, _event: Event) => {
                        /* istanbul ignore next */
                        const customEvent = new CustomEvent('custom-event', {
                            detail: {
                                control_fields: {
                                    mparticle: {
                                        keys: {
                                            source_section: 'source_section',
                                        },
                                        mp_event_type: 'Other',
                                    }
                                },
                                event_name: 'click_to_section',
                                source_section: 'reviews',
                            },
                        })
                        document.dispatchEvent(customEvent)
                    }
                },
                selector: 'data-sso-reviews',
            },
            topicFollow: {
                    attrs: {
                    display: 'inline',
                    followed: 'false',
                    topicId: '',
                    topicName: '',
                    topicType: '',
                },
                customEventHandlers: {
                    click: (_widget: ViafouraWidget, event: Event) => {
                        const followAttr = _widget.attrs.find((attr) => attr.key === 'followed')?.value

                        if (followAttr === 'true') {
                            logger('debug', 'Topic followed', event)
                        } else {
                            logger('debug', 'Topic unfollowed', event)
                        }
                    }
                },
                selector: 'data-sso-topic-follow'
            },
        }
    }

    private commentsCount = 0 // keep track of the number of comments posted
    private totalAllowedComments = -1 // total number of comments allowed ( -1 is unlimited)

    private viafouraClickListeners: { [key: string]: (...args: any[]) => void } = {
        'trending-list__title': this.displayConsole,
        'vf-js-tray-activator': this.displayConsole,
    }

    /**
     * Send login analytics
     * @param {string} signInMethod: social login provider name, or 'traditionalSignin'
     */
    public static sendLoginAnalytics(signInMethod: string) {
        document.dispatchEvent(
            new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        mparticle: {
                            keys: {
                                integration_name: 'integration_name',
                                sign_in_method: 'sign_in_method'
                            },
                            mp_event_type: 'UserContent'
                        },
                    },
                    event_name: AnalyticsService.customEventNames.signin,
                    integration_name: 'Viafoura',
                    sign_in_method: signInMethod
                },
            })
        )
    }

    /**
     * Send registration analytics event
     * @param {string} registrationMethod: registration provider name, or 'traditionalSignin'
     */
    public static sendRegistrationAnalytics(registrationMethod: string) {
        document.dispatchEvent(
            new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        mparticle: {
                            keys: {
                                integration_name: 'integration_name',
                                registration_method: 'registration_method'
                            },
                            mp_event_type: 'UserContent'
                        },
                    },
                    event_name: AnalyticsService.customEventNames.registration,
                    integration_name: 'Viafoura',
                    registration_method: registrationMethod
                },
            })
        )
    }

    /**
     * get vf comment text from localstorage, from before redirect to sign-in page
     * if it exists, populate the textarea with the value
     */
    public static async PopulateCommentSection(): Promise<void> {
        const vfCommentText = localStorage.getItem(Store.VF_COMMENT_TEXT)
        if (vfCommentText) {
            logger('debug', 'populating viafoura comment textarea with: ' + vfCommentText)
            // tslint:disable-next-line: max-line-length
            const vfCommentTextArea = document.querySelector('.vf-content-editor-input') as HTMLTextAreaElement
            if (vfCommentTextArea) {
                vfCommentTextArea.value = vfCommentText
                logger('debug', 'populated vf comment textarea successfully')
            } else {
                logger('debug', 'unable to populate vf comment textarea, cannot find element')
            }
            // remove the value from localstorage
            localStorage.removeItem(Store.VF_COMMENT_TEXT)
        }
    }

    public static ScrollToComments() {
        // if query param "comments" exists in URL, scroll to section with id "comments"
        const queryParams = new URLSearchParams(window.location.search)
        if (queryParams.has('comments')) {
            const commentsSection = document.getElementById('comments')
            if (commentsSection) {
                commentsSection.scrollIntoView()
            }
        }
    }

    /**
     * Get the target url and anchor text from the clicked target in the most commented widget
     * The structure of the relevant part of the widget is:
     *  <a href="targetUrl">
     *      <div>
     *          <div>
     *              <img> </img>
     *          </div>
     *          <div>
     *              <p>anchorText</p>
     *              <p> <svg> </svg> x comments </p>
     *          </div>
     *      </div>
     *  </a>
     *
     * @param {HTMLAnchorElement} target
     * @returns {object} - object with targetUrl and anchorText
     */
    public scrapeTargetUrlAndAnchorText(target: HTMLElement): { targetUrl: string; anchorText: string } {
        let parent = target // initialize search for parent anchor tag
        const maxParentLevels = 7 // 4 should be enough, but just in case the structure changes in the future
        let parentCount = 0

        while (parent !== null && parentCount < maxParentLevels) {
            if (parent.tagName.toLowerCase() === 'a') {
                // Found the first parent of type <a>
                const targetUrl = (parent.getAttribute('href') || '').split('?')[0]
                // get the p tag within a div with class vf-primary-text
                const anchorText = parent.querySelector('.vf-primary-text p')?.textContent?.trim() || ''
                return { targetUrl, anchorText }
            }
            parent = parent.parentElement
            parentCount++
        }
        return { targetUrl: '', anchorText: '' }
    }
    /**
     * Scrape the initial number of comments from the commenting widget
     * The structure of the relevant part of the widget is:
     * <nav>
     *   <span> </span>
     *   <span>
     *      <span> All comments </span>
     *      <span>
     *          <span> 200 </span>
     *      </span>
     *   </span>
     * </nav>
     *
     * @returns {number} - the number of comments
     */
    public scrapeInitialNumberOfComments(): number {
        if (this.commentWidget && this.commentWidget.el) {
            const firstNav = this.commentWidget.el.querySelector('nav') // get first nav element in commenting widget
            if (firstNav) {
                const spanElements = firstNav.querySelectorAll('span') // get all span elements
                const targetIndex = 4 // the 5th span element contains the number of comments
                if (spanElements.length > targetIndex) {
                    const commentsCount = spanElements[targetIndex].textContent.trim()
                    return parseInt(commentsCount, 10) || 0
                }
            }
        }
        return 0
    }

    public closeConversation() {
        if (this.commentWidget && this.commentWidget.el) {
            this.commentWidget.el.classList.add('commenting-closed') // hide reply buttons and forms
            // look for the top level comment text area and disable it
            const textareaParent = this.commentWidget.el.querySelector('.vf-post-form__new-content') as HTMLElement
            if (textareaParent) {
                const textarea = textareaParent.querySelector('textarea') as HTMLTextAreaElement
                // if the conversation is not already closed by viafoura
                if (textarea && textarea.getAttribute('placeholder') !== 'The conversation is now closed') {
                    textarea.setAttribute('placeholder', 'The conversation is now closed')
                    textarea.setAttribute('aria-label', 'The conversation is now closed')
                    textarea.setAttribute('disabled', 'disabled')
                    textarea.setAttribute('style', 'overflow-x: hidden; overflow-wrap: break-word; height: 25px;')
                }
            }
        }
    }

    public blockCommenting() {
        if (this.commentWidget && this.commentWidget.el) {
            this.commentWidget.el.classList.add('commenting-closed') // hide reply buttons and forms
            // look for the top level comment text area and remove it
            const textareaParent = this.commentWidget.el.querySelector('.vf-post-form__new-content') as HTMLElement
            if (textareaParent) {
                const textarea = textareaParent.querySelector('textarea') as HTMLTextAreaElement
                // if the conversation is not already closed by viafoura or by our comments limiting feature
                if (textarea && textarea.getAttribute('placeholder') !== 'The conversation is now closed') {
                    textareaParent.remove()
                }
            }
        }
    }

    public articleBlockerDisplayed(contentBlocker: Element): boolean {
        // did piano script remove 'hidden' from its class list
        if (!contentBlocker.classList.contains('hidden')) {
            // there are 3 content access blockers: anonymous, register and premium
            // we are only interested in the last 2, because the first one should not change commenting experience
            const anonymousBlocker = contentBlocker.querySelector('.intro-body__anonymous')
            // when anonymous blocker is hidden by piano, one of the other 2 is displayed by piano
            if (anonymousBlocker && anonymousBlocker.classList.contains('hidden')) {
                return true
            }
        }
        return false
    }

    public handleCommentingBlockedByPaywall() {
        // find the article blocker container
        const contentBlocker = document.querySelector('#article-block')
        if (contentBlocker) {
            if (this.articleBlockerDisplayed(contentBlocker)) {
                // article blocker already displayed, block commenting
                this.blockCommenting()
            } else {
                // keep an eye on the article blocker
                const observer = new MutationObserver((_mutations) => {
                    if (this.articleBlockerDisplayed(contentBlocker)) {
                        this.blockCommenting()
                    }
                })
                observer.observe(contentBlocker, { attributes: true, attributeFilter: ['class'] })
            }
        }
    }

    public handleCommentingBlockedByEmailVerification() {
        const profile = LocalStore.get<IProfile>(Store.ID)
        if (profile.email && !profile.emailConfirmed) {
            this.blockCommenting()
            this.addEmailVerificationModal()
        }
    }

    public addEmailVerificationModal() {
        if (this.commentWidget && this.commentWidget.el) {
            const modal = document.createElement('div')
            modal.classList.add('email-verification-card')
            modal.innerHTML = `
              <section>
                  <h3>Verify your email</h3>
                  <h4>Help keep your account safe</h4>
                  <p>You will be sent a link to verify your email address after clicking the button.</p>
              </section>
              <section>
                  <button class="button button--primary" type="button" id="email-verification-button">
                    Verify now
                  </button>
              </section>
            `
            this.commentWidget.el.insertBefore(modal, this.commentWidget.el.children[1])
            modal.onclick = (event) => {
                if ((event.target as HTMLButtonElement).id === 'email-verification-button') {
                    modal.innerHTML = `
                    <div class="close-icon-container">
                        <svg id="email-verification-close" width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M15.3625 8.875L13 11.2375L10.6375 8.875C10.15 8.3875 9.3625 8.3875 8.875 8.875C8.3875 9.3625 8.3875 10.15 8.875 10.6375L11.2375 13L8.875 15.3625C8.3875 15.85 8.3875 16.6375 8.875 17.125C9.3625 17.6125 10.15 17.6125 10.6375 17.125L13 14.7625L15.3625 17.125C15.85 17.6125 16.6375 17.6125 17.125 17.125C17.6125 16.6375 17.6125 15.85 17.125 15.3625L14.7625 13L17.125 10.6375C17.6125 10.15 17.6125 9.3625 17.125 8.875C16.6375 8.4 15.8375 8.4 15.3625 8.875ZM13 0.5C6.0875 0.5 0.5 6.0875 0.5 13C0.5 19.9125 6.0875 25.5 13 25.5C19.9125 25.5 25.5 19.9125 25.5 13C25.5 6.0875 19.9125 0.5 13 0.5ZM13 23C7.4875 23 3 18.5125 3 13C3 7.4875 7.4875 3 13 3C18.5125 3 23 7.4875 23 13C23 18.5125 18.5125 23 13 23Z" fill="black"/>
                        </svg>
                    </div>

                    <h3>An email is on the way</h3>
                    <h4>Check your inbox to finish verifying your address</h4>
                    <div id="resendemailverification-container"></div>
                    `
                    modal.classList.add('sent')

                    const resendVerificationEmailEvent = new CustomEvent(IdentityEvent.RESEND_VERIFICATION_EMAIL)
                    document.dispatchEvent(resendVerificationEmailEvent)
                }

                if ((event.target as SVGElement).id === 'email-verification-close') {
                    modal.remove()
                }
            }
        }
    }

    public enable() {
        this.addSubscriptions()
        this.addListeners()
    }

    public addSubscriptions() {
        window.vfQ.push(() => {
            this.buildSubscriptions()
        })
    }

    public buildSubscriptions() {
        if (this.commentWidget && this.displayAds) {
            logger('debug', 'Viafoura $subscribe bind comment Ad Slot')
            // subscribe to the viafoura event fired when the ad slot is available in the comment widget
            window.vf.$subscribe(
                'vf-ads',
                'requestCommentAd',
                // bind the correct scope to handle the ad slot for the widget
                this.commentWidget.handleAdSlotEvent.bind(this.commentWidget)
            )
        }
        if (this.carouselWidget && this.displayAds) {
            logger('debug', 'Viafoura $subscribe bind carousel Ad Slot')
            // subscribe to the viafoura event fired when the ad slot is available in the carousel widget
            window.vf.$subscribe(
                'vf-ads',
                'requestContentRecirculationAd',
                // bind the correct scope to handle the ad slot for the widget
                this.carouselWidget.handleAdSlotEvent.bind(this.carouselWidget)
            )
        }

        window.vf.$subscribe('logout', 'success', () => {
            logger('debug', 'Viafoura $subscribe logout success')
            document.dispatchEvent(Utils.event(IdentityEvent.COMMENT_LOGOUT))
        })
        /* istanbul ignore next */
        window.vf.$subscribe('comment', 'created', () => {
            const topCommenterHolder = document.querySelector('.top-commenter-offer') as HTMLElement
            if (topCommenterHolder) {
                topCommenterHolder.setAttribute('data-evt-skip-click', 'true')
                topCommenterHolder.click()
            }
            // update number of comments (even though it fires only for comments created by this user)
            this.commentsCount++
            if (this.totalAllowedComments > -1 && this.commentsCount >= this.totalAllowedComments) {
                // max number of comments reached
                this.closeConversation()
            }
        })
        /* istanbul ignore next */
        window.vf.$subscribe('comment-reply', 'posted', () => {
            // update number of comments (even though it fires only for comments created by this user)
            this.commentsCount++
            if (this.totalAllowedComments > -1 && this.commentsCount >= this.totalAllowedComments) {
                // max number of comments reached
                this.closeConversation()
            }
        })

        /* istanbul ignore next */
        window.vf.$subscribe('commenting', 'loaded', () => {
            const newCommentsSection = document.querySelector('.v3-comments__post-form') as HTMLElement
            const topCommenterHolder = document.createElement('div')
            topCommenterHolder.className = 'top-commenter-offer'
            topCommenterHolder.style.margin = '-10px 0px 0px 0px'
            newCommentsSection.appendChild(topCommenterHolder)
            const topCommenterReminderHolder = document.createElement('div')
            topCommenterReminderHolder.className = 'top-commenter-reminder'
            topCommenterReminderHolder.style.margin = '-10px 0px 0px 0px'
            topCommenterReminderHolder.classList.add('hidden')
            newCommentsSection.appendChild(topCommenterReminderHolder)

            // Dynamically limit max number of comments posted (not available as a setting in viafoura)
            // get the initial number of comments as the widget is loaded
            this.commentsCount = this.scrapeInitialNumberOfComments()
            if (this.commentWidget && this.commentWidget.el) {
                this.totalAllowedComments = parseInt(this.commentWidget.el.dataset.totalAllowedComments, 10) || -1
            }
            if (this.totalAllowedComments > -1 && this.commentsCount >= this.totalAllowedComments) {
                // max number of comments reached
                this.closeConversation()
            }
            // Prevent users from posting comments when article blocker is displayed
            // check if the client option is set for this feature
            if (this.commentWidget && this.commentWidget.el) {
                if ('commentingBlockedByPaywall' in this.commentWidget.el.dataset) {
                    this.handleCommentingBlockedByPaywall()
                }
                if ('commentingRequiresEmailVerification' in this.commentWidget.el.dataset) {
                    this.handleCommentingBlockedByEmailVerification()
                }
            }
        })
        window.vf.$prepublish((channel: string, event: string, ...args: object[]) => {
            if (channel === 'authentication' && (event === 'required' || event === 'needed')) {
                logger('debug', 'Viafoura $prepublish authentication required')
                // get value of textarea with class vf-content-editor-input and store it in localstorage
                const textarea = document.querySelector<HTMLTextAreaElement>('.vf-content-editor-input')
                if (textarea) {
                    localStorage.setItem(Store.VF_COMMENT_TEXT, textarea.value)
                }
                const commentParam = location.search.length > 0 ? '&comments' : '?comments'
                const redirectUrl = location.href + commentParam
                document.dispatchEvent(Utils.event(IdentityEvent.COMMENT_LOGIN, { redirectUrl }))
                return false
            }

            return { channel, event, args }
        })
    }

    public addListeners() {
        document.addEventListener('click', async (e: Event) => {
            const el = e.target as Element
            for (const target in this.viafouraClickListeners) {
                if (el.classList.contains(target)) {
                    this.eventFunction(this.viafouraClickListeners[target], target)
                }
            }
        })
        const commentsSection = document.querySelector('.comments__section-viafoura-container') as HTMLElement
        if (commentsSection) {
            commentsSection.addEventListener('click', async () => {
                logger('debug', 'Viafoura clicked - populating comments section')
                await Viafoura.PopulateCommentSection()
            })
        }
    }

    /**
     * Remove ads from any registered widgets
     */
    public removeCommentWidgetsAdUI() {
        this.displayAds = false
        for (const widget of Object.values(this.DOM.wrappers)) {
            this.updateAdlightUserStatus(widget, this.displayAds)
            logger('debug', 'Viafoura set adLight status for: ' + widget)
        }
    }

    /**
     * Update adLight class to widget container if valid adLight user
     * @param widget
     * @param displayAds
     */
    public updateAdlightUserStatus(widget: any, displayAds: boolean) {
        const elArr = document.querySelectorAll<HTMLInputElement>(`[${widget.selector}]`)

        if (elArr && !displayAds) {
            for (const el of elArr) {
                DOMUtils.addClass(el, this.DOM.classes.adLight)
                logger('debug', `viafoura remove ads from: ${el}`)
            }
        } else if (elArr && displayAds) {
            for (const el of elArr) {
                DOMUtils.removeClass(el, this.DOM.classes.adLight)
            }
        }
    }

    public displayConsole(target: string) {
        logger('debug', 'target: ', target)
    }

    public eventFunction(event: any, target: string) {
        event(target)
    }

    /**
     * Initialize commenting features
     *
     * @param conf
     * @param accessToken  - normally empty. populated with Login Radius token if present
     * @returns
     */
    public async initialize(conf?: IAdsConfiguration, accessToken?: string) {
        if (window.vfQ) {
            return this
        }

        window.vf = window.vf || { events: {} }
        window.vfQ = window.vfQ || []

        let commentAdConfiguration: ICommentAdConfiguration

        if (conf) {
            commentAdConfiguration = conf.commentAdConfiguration
        }

        if (commentAdConfiguration) {
            this.DOM.wrappers.commenting.attrs.firstPromoPosition = commentAdConfiguration.firstPromoPosition
                ? commentAdConfiguration.firstPromoPosition
                : '1'
            this.DOM.wrappers.commenting.attrs.promoInterval = commentAdConfiguration.promoInterval
                ? commentAdConfiguration.promoInterval
                : '10'
        }

        logger('debug', 'comment section is now in view')
        await DOMUtils.ready // wait for DOM
        // load viafoura script in pages with sign out link
        // it should not load on my topics sign-in page to avoid redirect issue on safari
        const dataSignOut = document.querySelector(`[${this.DOM.attrs.signOut}]`)
        const dataMyTopics = document.querySelector(`[${this.DOM.attrs.myTopics}]`)
        if (dataSignOut && !dataMyTopics) {
            await this.scriptLoad(this.DOM.attrs.jsUrl)
            logger('debug', 'Viafoura JS is now ready')
        }

        await this.validateViafouraRequired(this.DOM.wrappers)
        // get widget load status
        if (this.loadViafouraWidgets) {
            // load viafoura widgets
            for (const [type, widget] of Object.entries(this.DOM.wrappers)) {
                this.updateAdlightUserStatus(widget, this.displayAds)
                await this.loadViafouraUI(type as Widget, widget)
                logger('debug', 'Viafoura UI is now ready for: ' + widget)
            }
            // load viafoura scss
            await this.scssLoad()
            const commentMeterCookie =
                (document.cookie.split(';').find((c) => c.trim().startsWith(`${Store.COMMENT_METER}=`)) || '')
                    .trim()
                    .split('=')
                    .pop() || 'false'

            logger('debug', 'Viafoura CSS is now ready')
            window.vfQ.push(async () => {
                // get user info
                const user = await window.vf.context.get('user')
                logger('debug', `Viafoura user: ${user}`)

                // login to Viafoura if not logged in already (user.anonymous is 0, if logged in)
                if (accessToken && user.anonymous !== 0) {
                    logger('debug', `logging in viafoura user using token from FEM ${accessToken}`)
                    window.vf.session.login.loginRadius(accessToken)
                } else {
                    const localProfile = LocalStore.get<IProfile>(Store.ID)
                    if (accessToken && user && localProfile && localProfile.name !== user.name) {
                        window.vf.session.login.loginRadius(accessToken)
                    }
                }
                if (commentMeterCookie === 'expired') {
                    window.vf.$subscribe('commenting', 'loaded', () => {
                        const loadButtonElements = document.getElementsByClassName('vf-load-more__button')
                        if (loadButtonElements.length > 0) {
                            const loadButtonElement = loadButtonElements[0] as HTMLButtonElement
                            // block analytic event
                            loadButtonElement.setAttribute('data-evt-skip-click', 'true')
                            loadButtonElement.click()
                        }
                    })
                    if (this.commentWidget && this.commentWidget.el) {
                        const el = this.commentWidget.el.getElementsByTagName('vf-conversations')
                        if (el && el.length > 0) {
                            el[0].setAttribute('limit', '1')
                        }
                    }
                }

                this.enable()
                await DOMUtils.ready
                // temporary disabled auto scroll to comments
                // Viafoura.ScrollToComments()
            })
        }

        return this
    }

    /**
     * Signout viafoura if using login radius tray integration
     */
    public async doWidgetSignout() {
        if (window.vf && window.vf.session) {
            logger('info', 'signing out viafoura user')
            await window.vf.session.logout()
            logger('info', 'viafoura user signed out')
        }
    }

    /**
     * Validate a viafoura wrapper is present in DOM
     */
    public async validateViafouraRequired(wrappers: object) {
        // await this.hasSession() // wait for session
        // Cycle through supported wrapper object
        for (const attrs of Object.values(wrappers)) {
            // Search for supported wrapper selector in dom
            if (document.querySelector(`[${attrs.selector}]`)) {
                // If selector is found then viafoura is set to required
                this.loadViafouraWidgets = true
                return true
            }
        }
        return false
    }

    /**
     * Load viafoura css file from scssUrl
     */
    public async scssLoad(): Promise<boolean> {
        try {
            logger('debug', "viafoura loading css './styles/viafoura.scss'")
            const scss = import('./styles/viafoura.scss')
            DOMUtils.addStyles((await scss).default)
            return true
        } catch (err) {
            logger('error', 'Error loading viafoura css: ' + err)
            return false
        }
    }

    /**
     * Load viafoura js file from jsUrl
     */
    public async scriptLoad(jsUrl: string): Promise<boolean> {
        try {
            logger('debug', `viafoura loading script ${this.DOM.attrs.jsUrl}`)
            DOMUtils.loadScript(jsUrl)
            return true
        } catch (err) {
            logger('error', 'Error loading viafoura css: ' + err)
            return false
        }
    }

    /**
     * Load the available widgets as objects.  The ViafouraWidget class takes
     * care of the interaction with the ad slot
     */
    public async loadViafouraUI(type: Widget, widget: any) {
        try {
            // Get and set the element by selector
            const el = document.querySelector<HTMLInputElement>(`[${widget.selector}]`)
            if (el) {
                switch (type) {
                    case 'commenting':
                        logger('debug', 'viafoura creating commenting widget')

                        // Get initial properties of wrapper and store in object
                        widget.attrs.replyLimit = el.dataset.replyLimit
                        widget.attrs.commentsLimit = el.dataset.commentsLimit
                        widget.attrs.commentsLoadMoreLimit = el.dataset.commentsLoadMoreLimit
                        widget.attrs.signature = el.dataset.signature

                        // BT Issue -
                        // Create commenting widget
                        const widgetAttributes = [
                            {
                                key: 'first-promo-position',
                                value: widget.attrs.firstPromoPosition
                            },
                            {
                                key: 'promoInterval',
                                value: widget.attrs.promoInterval
                            },
                            {
                                key: 'limit',
                                value: widget.attrs.commentsLimit
                            },
                            {
                                key: 'pagination-limit',
                                value: widget.attrs.commentsLoadMoreLimit
                            },
                            {
                                key: 'reply-limit',
                                value: widget.attrs.replyLimit
                            },
                        ]

                        if (widget.attrs.signature && widget.attrs.signature.trim() !== '') {
                            widgetAttributes.push({
                                key: 'signature',
                                value: widget.attrs.signature
                            })
                        }

                        this.commentWidget = new ViafouraWidget(
                            this.DOM.wrappers.commenting.selector,
                            widgetAttributes,
                            this.DOM.widgets.conversations,
                            [`${this.DOM.widgets.trayTrigger}`],
                            2, // tslint:disable-line: Profile: no-magic-numbers
                            this.DOM.wrappers.commenting.customEventHandlers
                        )
                        return true

                    case 'carousel':
                        logger('debug', 'viafoura creating carousel widget')

                        // Create carousel widget
                        this.carouselWidget = new ViafouraWidget(
                            this.DOM.wrappers.carousel.selector,
                            [
                                {
                                    key: 'title',
                                    value: widget.title
                                },
                            ],
                            this.DOM.widgets.contentRecirculation,
                            undefined,
                            1,
                            this.DOM.wrappers.carousel.customEventHandlers
                        )
                        return true

                    case 'conversationStarter':
                        logger('debug', 'viafoura creating conversation starter widget')

                        // Create carousel widget
                        this.conversationStarterWidget = new ViafouraWidget(
                            this.DOM.wrappers.conversationStarter.selector,
                            [
                                {
                                    key: 'title',
                                    value: widget.title
                                },
                                {
                                    key: 'description',
                                    value: widget.description
                                },
                                {
                                    key: 'target',
                                    value: widget.target,
                                },
                            ],
                            this.DOM.widgets.conversationStarter,
                            undefined,
                            1,
                            this.DOM.wrappers.conversationStarter.customEventHandlers
                        )
                        return true

                    case 'reviews':
                        logger('debug', 'viafoura creating reviews widget')

                        // Create carousel widget
                        this.conversationStarterWidget = new ViafouraWidget(
                            this.DOM.wrappers.reviews.selector,
                            [],
                            this.DOM.widgets.reviews,
                            undefined,
                            1,
                            this.DOM.wrappers.reviews.customEventHandlers
                        )
                        return true

                    case 'topicFollow':
                        logger('debug', 'viafoura creating topic follow widget')

                        // Get topic ID
                        const topicId = el.dataset.topicId

                        if (!topicId) {
                            logger('warn', 'viafoura topic follow widget skipped: no topicId found')
                            return false
                        }

                        const topicName = el.dataset.topicName || 'Topic'
                        const topicType = el.dataset.topicType || 'topic'

                        // Set widget attributes
                        widget.attrs.topicId = topicId
                        widget.attrs.topicName = topicName
                        widget.attrs.topicType = topicType
                        widget.attrs.display = el.dataset.display

                        // Build ViafouraWidget attributes
                        const topicFollowAttributes = [
                        { key: 'topic-id', value: widget.attrs.topicId },
                        { key: 'topic-name', value: widget.attrs.topicName },
                        { key: 'topic-type', value: widget.attrs.topicType },
                        { key: 'display', value: widget.attrs.display },
                        ]

                        // Create the Topic Follow widget
                        this.topicFollow = new ViafouraWidget(
                        this.DOM.wrappers.topicFollow.selector,
                        topicFollowAttributes,
                        this.DOM.widgets.topicFollow,
                        undefined,
                        1,
                        this.DOM.wrappers.topicFollow.customEventHandlers,
                        true
                        )
                        return true

                }
            }
            logger('debug', `viafoura widget selector not found: ${widget.selector}`)
            return false
        } catch (err) {
            logger('error', `viafoura create ${widget.selector} error: ${err}`)
            return false
        }
    }
}
